import React, { useEffect, useState, useCallback } from 'react';
import styles from './GebiedsanalyseModule.module.css';
import {
  Project, GeoJsonPolygon, GeoJsonPoint, DemographicProfile, StakeholderAdvies,
  GeocodeSuggestion, OmgevingsData, BedrijvenData, BedrijvenAdvies, PersonaHint,
  AdditionalDemographics, VoorzieningenData, BedrijvenOverpassData, OrganisatiesData,
  Gebiedsprofiel, InvloedsgebiedAnalyse, WoningCategorie, RaakvlakAnalyse,
} from '../../types';
import { analysisAPI } from '../../services/api';
import { AnalysisMap, MapMarker } from './AnalysisMap';
import { AnalysisSidebar } from './AnalysisSidebar';
import { OmgevingsDataFilter } from './OmgevingsDataFilter';
import { ImportToRelatiebeheer } from './ImportToRelatiebeheer';
import { useToast } from '../../contexts/ToastContext';

interface GebiedsanalyseModuleProps {
  project: Project;
}

export const GebiedsanalyseModule: React.FC<GebiedsanalyseModuleProps> = ({ project }) => {
  const toast = useToast();

  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [showMap, setShowMap] = useState(true);
  const [areaId, setAreaId] = useState<string | null>(null);
  const [boundary, setBoundary] = useState<GeoJsonPolygon | null>(null);
  const [bufferZone, setBufferZone] = useState<GeoJsonPolygon | null>(null);
  const [influenceRadius, setInfluenceRadius] = useState(500);
  const [demographics, setDemographics] = useState<DemographicProfile | null>(null);
  const [buurtNaam, setBuurtNaam] = useState<string | null>(null);
  const [adviezen, setAdviezen] = useState<StakeholderAdvies[]>([]);
  const [areaSqm, setAreaSqm] = useState<number | null>(null);
  const [bedrijven, setBedrijven] = useState<BedrijvenData | null>(null);
  const [bedrijvenAdviezen, setBedrijvenAdviezen] = useState<BedrijvenAdvies[]>([]);
  const [personaHints, setPersonaHints] = useState<PersonaHint[]>([]);
  const [additionalDemographics, setAdditionalDemographics] = useState<AdditionalDemographics | null>(null);

  // Extended gebiedsdata state
  const [voorzieningen, setVoorzieningen] = useState<VoorzieningenData | null>(null);
  const [bedrijvenOverpass, setBedrijvenOverpass] = useState<BedrijvenOverpassData | null>(null);
  const [organisaties, setOrganisaties] = useState<OrganisatiesData | null>(null);
  const [gebiedsprofiel, setGebiedsprofiel] = useState<Gebiedsprofiel | null>(null);
  const [profielLoading, setProfielLoading] = useState(false);

  // Invloedsgebied analyse
  const [invloedsgebiedAnalyse, setInvloedsgebiedAnalyse] = useState<InvloedsgebiedAnalyse | null>(null);
  const [invloedsgebiedLoading, setInvloedsgebiedLoading] = useState(false);
  const [woningFilters, setWoningFilters] = useState<WoningCategorie[]>([]);
  const [raakvlakAnalyse, setRaakvlakAnalyse] = useState<RaakvlakAnalyse | null>(null);
  const [raakvlakLoading, setRaakvlakLoading] = useState(false);

  const [mapCenter, setMapCenter] = useState<[number, number]>([52.1326, 5.2913]);
  const [mapZoom, setMapZoom] = useState(8);

  // Import dialog state
  const [showImportDialog, setShowImportDialog] = useState(false);

  // Markers state
  const [markers, setMarkers] = useState<MapMarker[]>([]);

  // Omgevingsdata state
  const [omgevingsData, setOmgevingsData] = useState<OmgevingsData | null>(null);
  const [omgevingsLoading, setOmgevingsLoading] = useState(false);
  const [activeFilters, setActiveFilters] = useState<Record<string, boolean>>({
    meldingen: true,
    werkzaamheden: true,
    vergunningen: true,
    voorzieningen: false,
    bedrijven_osm: false,
    organisaties: false,
    panden: false,
  });

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<GeocodeSuggestion[]>([]);
  const [showResults, setShowResults] = useState(false);

  // Fetch raakvlakprojecten analyse
  const fetchRaakvlakprojecten = useCallback(async (currentAreaId: string) => {
    setRaakvlakLoading(true);
    try {
      const data = await analysisAPI.getRaakvlakprojecten(currentAreaId);
      setRaakvlakAnalyse(data);
    } catch {
      // Non-critical
    } finally {
      setRaakvlakLoading(false);
    }
  }, []);

  // Fetch invloedsgebied analyse for the project area
  const fetchInvloedsgebiedAnalyse = useCallback(async (currentAreaId: string, filters?: WoningCategorie[]) => {
    setInvloedsgebiedLoading(true);
    try {
      const data = await analysisAPI.getInvloedsgebiedAnalyse(currentAreaId, filters);
      setInvloedsgebiedAnalyse(data);
    } catch {
      // Non-critical
    } finally {
      setInvloedsgebiedLoading(false);
    }
  }, []);

  // Fetch omgevingsdata for the project area
  const fetchOmgevingsData = useCallback(async () => {
    setOmgevingsLoading(true);
    try {
      const data = await analysisAPI.getOmgevingsdata(project.id);
      setOmgevingsData(data);
    } catch {
      // Omgevingsdata is non-critical
    } finally {
      setOmgevingsLoading(false);
    }
  }, [project.id]);

  const handleFilterToggle = useCallback((key: string) => {
    setActiveFilters(prev => ({ ...prev, [key]: !prev[key] }));
  }, []);

  // Load existing area analysis on mount
  useEffect(() => {
    loadExistingAnalysis();
  }, [project.id]);

  const loadExistingAnalysis = async () => {
    try {
      const data = await analysisAPI.getArea(project.id);
      if (data.projectArea) {
        setAreaId(data.projectArea._id);
        setBoundary(data.projectArea.boundary);
        setBufferZone(data.projectArea.bufferZone);
        setInfluenceRadius(data.projectArea.influenceRadius);
        setDemographics(data.demographics);
        setBuurtNaam(data.buurtNaam);
        setAdviezen(data.adviezen || []);
        setAreaSqm(data.areaSqm);
        setBedrijven(data.bedrijven || null);
        setBedrijvenAdviezen(data.bedrijvenAdviezen || []);
        setPersonaHints(data.personaHints || []);
        setAdditionalDemographics(data.additionalDemographics || null);
        setVoorzieningen(data.voorzieningen || null);
        setBedrijvenOverpass(data.bedrijvenOverpass || null);
        setOrganisaties(data.organisaties || null);
        setGebiedsprofiel(data.gebiedsprofiel || null);

        // Center map on the project area
        if (data.projectArea.location?.coordinates) {
          const [lng, lat] = data.projectArea.location.coordinates;
          setMapCenter([lat, lng]);
          setMapZoom(14);
        }

        // Load saved markers
        if (data.projectArea.markers) {
          setMarkers(data.projectArea.markers);
        }

        // Fetch omgevingsdata, invloedsgebied analyse en raakvlakprojecten in background
        fetchOmgevingsData();
        fetchInvloedsgebiedAnalyse(data.projectArea._id);
        fetchRaakvlakprojecten(data.projectArea._id);
      }
    } catch {
      // No existing analysis — that's fine
    } finally {
      setInitialLoading(false);
    }
  };

  const handleShapeCreated = useCallback(async (geojson: GeoJsonPolygon) => {
    setLoading(true);
    setBoundary(geojson);

    try {
      if (areaId) {
        // Update existing area
        const data = await analysisAPI.updateArea(areaId, {
          boundary: geojson,
          influenceRadius,
        });
        setAreaId(data.projectArea._id);
        setBufferZone(data.projectArea.bufferZone);
        setDemographics(data.demographics);
        setBuurtNaam(data.buurtNaam);
        setAdviezen(data.adviezen || []);
        setAreaSqm(data.areaSqm);
        setBedrijven(data.bedrijven || null);
        setBedrijvenAdviezen(data.bedrijvenAdviezen || []);
        setPersonaHints(data.personaHints || []);
        setAdditionalDemographics(data.additionalDemographics || null);
        setVoorzieningen(data.voorzieningen || null);
        setBedrijvenOverpass(data.bedrijvenOverpass || null);
        setOrganisaties(data.organisaties || null);
        setGebiedsprofiel(data.gebiedsprofiel || null);
        toast.success('Gebiedsanalyse bijgewerkt');
        fetchOmgevingsData();
        fetchInvloedsgebiedAnalyse(data.projectArea._id);
        fetchRaakvlakprojecten(data.projectArea._id);
      } else {
        // Create new area
        const data = await analysisAPI.createArea(project.id, {
          name: `Projectgebied ${project.name}`,
          boundary: geojson,
          influenceRadius,
        });
        setAreaId(data.projectArea._id);
        setBufferZone(data.projectArea.bufferZone);
        setDemographics(data.demographics);
        setBuurtNaam(data.buurtNaam);
        setAdviezen(data.adviezen || []);
        setAreaSqm(data.areaSqm);
        setBedrijven(data.bedrijven || null);
        setBedrijvenAdviezen(data.bedrijvenAdviezen || []);
        setPersonaHints(data.personaHints || []);
        setAdditionalDemographics(data.additionalDemographics || null);
        setVoorzieningen(data.voorzieningen || null);
        setBedrijvenOverpass(data.bedrijvenOverpass || null);
        setOrganisaties(data.organisaties || null);
        setGebiedsprofiel(data.gebiedsprofiel || null);
        toast.success('Gebiedsanalyse voltooid');
        fetchOmgevingsData();
        fetchInvloedsgebiedAnalyse(data.projectArea._id);
        fetchRaakvlakprojecten(data.projectArea._id);
      }
    } catch (error: any) {
      const message = error.response?.data?.error || 'Kon gebiedsanalyse niet uitvoeren';
      toast.error('Fout bij analyse', message);
    } finally {
      setLoading(false);
    }
  }, [areaId, influenceRadius, project.id, project.name, toast, fetchOmgevingsData]);

  const handleShapeEdited = useCallback(async (geojson: GeoJsonPolygon) => {
    if (!areaId) return;

    setBoundary(geojson);
    setLoading(true);

    try {
      const data = await analysisAPI.updateArea(areaId, {
        boundary: geojson,
        influenceRadius,
      });
      setBufferZone(data.projectArea.bufferZone);
      setDemographics(data.demographics);
      setBuurtNaam(data.buurtNaam);
      setAdviezen(data.adviezen || []);
      setAreaSqm(data.areaSqm);
      setBedrijven(data.bedrijven || null);
      setBedrijvenAdviezen(data.bedrijvenAdviezen || []);
      setPersonaHints(data.personaHints || []);
      setAdditionalDemographics(data.additionalDemographics || null);
      toast.success('Projectgebied bijgewerkt');
    } catch (error: any) {
      const message = error.response?.data?.error || 'Kon gebiedsanalyse niet bijwerken';
      toast.error('Fout bij bijwerken', message);
    } finally {
      setLoading(false);
    }
  }, [areaId, influenceRadius, toast]);

  const handleRadiusChange = useCallback(async (newRadius: number) => {
    setInfluenceRadius(newRadius);

    if (!areaId) return;

    try {
      const data = await analysisAPI.updateArea(areaId, { influenceRadius: newRadius });
      setBufferZone(data.projectArea.bufferZone);
      fetchInvloedsgebiedAnalyse(areaId);
    } catch {
      // Silently fail on radius updates
    }
  }, [areaId, fetchInvloedsgebiedAnalyse]);

  const handleRefresh = useCallback(async () => {
    if (!areaId) return;

    setLoading(true);
    try {
      const data = await analysisAPI.refreshData(areaId);
      setDemographics(data.demographics);
      setBuurtNaam(data.buurtNaam);
      setAdviezen(data.adviezen || []);
      setBedrijven(data.bedrijven || null);
      setBedrijvenAdviezen(data.bedrijvenAdviezen || []);
      setPersonaHints(data.personaHints || []);
      setAdditionalDemographics(data.additionalDemographics || null);
      setVoorzieningen(data.voorzieningen || null);
      setBedrijvenOverpass(data.bedrijvenOverpass || null);
      setOrganisaties(data.organisaties || null);
      setGebiedsprofiel(data.gebiedsprofiel || null);
      if (areaId) {
        fetchInvloedsgebiedAnalyse(areaId);
        fetchRaakvlakprojecten(areaId);
      }
      toast.success('Data vernieuwd');
    } catch (error: any) {
      const message = error.response?.data?.error || 'Kon data niet vernieuwen';
      toast.error('Fout bij vernieuwen', message);
    } finally {
      setLoading(false);
    }
  }, [areaId, toast]);

  const handleDeleteArea = useCallback(async () => {
    if (!areaId) return;

    const confirmed = window.confirm('Weet je zeker dat je het projectgebied en alle bijbehorende analyse wilt verwijderen?');
    if (!confirmed) return;

    setLoading(true);
    try {
      await analysisAPI.deleteArea(areaId);
      setAreaId(null);
      setBoundary(null);
      setBufferZone(null);
      setDemographics(null);
      setBuurtNaam(null);
      setAdviezen([]);
      setAreaSqm(null);
      setBedrijven(null);
      setBedrijvenAdviezen([]);
      setPersonaHints([]);
      setAdditionalDemographics(null);
      setMarkers([]);
      setOmgevingsData(null);
      setVoorzieningen(null);
      setBedrijvenOverpass(null);
      setOrganisaties(null);
      setGebiedsprofiel(null);
      setInvloedsgebiedAnalyse(null);
      setWoningFilters([]);
      setRaakvlakAnalyse(null);
      setMapCenter([52.1326, 5.2913]);
      setMapZoom(8);
      toast.success('Projectgebied verwijderd');
    } catch (error: any) {
      const message = error.response?.data?.error || 'Kon projectgebied niet verwijderen';
      toast.error('Fout bij verwijderen', message);
    } finally {
      setLoading(false);
    }
  }, [areaId, toast]);

  // Persist markers to backend
  const saveMarkersToBackend = useCallback(async (updatedMarkers: MapMarker[]) => {
    if (!areaId) return;
    try {
      await analysisAPI.saveMarkers(areaId, updatedMarkers);
    } catch {
      // Non-critical: markers still work locally
    }
  }, [areaId]);

  // Marker handlers
  const handleMarkerAdded = useCallback((position: GeoJsonPoint, label: string) => {
    const newMarker: MapMarker = {
      id: `marker-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`,
      position,
      label,
      type: 'flag',
    };
    setMarkers(prev => {
      const updated = [...prev, newMarker];
      saveMarkersToBackend(updated);
      return updated;
    });
    toast.success('Markering geplaatst', label);
  }, [toast, saveMarkersToBackend]);

  const handleMarkerRemoved = useCallback((markerId: string) => {
    setMarkers(prev => {
      const updated = prev.filter(m => m.id !== markerId);
      saveMarkersToBackend(updated);
      return updated;
    });
    toast.success('Markering verwijderd');
  }, [toast, saveMarkersToBackend]);

  // Woningfilter change handler
  const handleWoningFilterChange = useCallback((filters: WoningCategorie[]) => {
    setWoningFilters(filters);
    if (areaId) {
      fetchInvloedsgebiedAnalyse(areaId, filters);
    }
  }, [areaId, fetchInvloedsgebiedAnalyse]);

  // Regenerate AI gebiedsprofiel
  const handleRegenerateProfiel = useCallback(async (customInstruction?: string) => {
    if (!areaId) return;
    setProfielLoading(true);
    try {
      const data = await analysisAPI.generateProfiel(areaId, customInstruction);
      setGebiedsprofiel(data.gebiedsprofiel || null);
      toast.success('Gebiedsprofiel opnieuw gegenereerd');
    } catch (error: any) {
      const message = error.response?.data?.error || 'Kon gebiedsprofiel niet genereren';
      toast.error('Fout bij genereren', message);
    } finally {
      setProfielLoading(false);
    }
  }, [areaId, toast]);

  // Geocode search
  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    try {
      const results = await analysisAPI.geocode(searchQuery);
      setSearchResults(results);
      setShowResults(true);
    } catch {
      toast.error('Zoeken mislukt', 'Kon de locatie niet vinden via PDOK');
    }
  };

  const handleSearchSelect = (result: GeocodeSuggestion) => {
    setMapCenter([result.lat, result.lon]);
    setMapZoom(15);
    setShowResults(false);
    setSearchQuery(result.display);
  };

  if (initialLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingFull}>
          <div className={styles.spinner} />
          <p>Gebiedsanalyse laden...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Compact toolbar: search + map toggle */}
      <div className={styles.toolbar}>
        <div className={styles.searchBarCompact}>
          <input
            type="text"
            placeholder="Zoek adres of locatie..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            className={styles.searchInputCompact}
          />
          <button onClick={handleSearch} className={styles.searchButtonCompact}>
            Zoeken
          </button>
          {showResults && searchResults.length > 0 && (
            <div className={styles.searchResults}>
              {searchResults.map((result, i) => (
                <button
                  key={i}
                  className={styles.searchResultItem}
                  onClick={() => handleSearchSelect(result)}
                >
                  <span className={styles.resultType}>{result.type}</span>
                  {result.display}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Omgevingsdata Filter Bar (inline) */}
        {boundary && (
          <OmgevingsDataFilter
            activeFilters={activeFilters}
            onToggle={handleFilterToggle}
            data={omgevingsData}
            loading={omgevingsLoading}
            voorzieningenCount={voorzieningen?.items?.length || 0}
            bedrijvenOsmCount={bedrijvenOverpass?.items?.length || 0}
            organisatiesCount={organisaties?.items?.length || 0}
          />
        )}

        <button
          className={`${styles.mapToggle} ${!showMap ? styles.mapToggleActive : ''}`}
          onClick={() => setShowMap(prev => !prev)}
          title={showMap ? 'Kaart verbergen' : 'Kaart tonen'}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            {showMap ? (
              <>
                <rect x="3" y="3" width="7" height="7" />
                <rect x="14" y="3" width="7" height="7" />
                <rect x="3" y="14" width="7" height="7" />
                <rect x="14" y="14" width="7" height="7" />
              </>
            ) : (
              <>
                <polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6" />
                <line x1="8" y1="2" x2="8" y2="18" />
                <line x1="16" y1="6" x2="16" y2="22" />
              </>
            )}
          </svg>
          {showMap ? 'Overzicht' : 'Kaart'}
        </button>
      </div>

      <div className={`${styles.content} ${!showMap ? styles.contentFullWidth : ''}`}>
        {/* Map Section */}
        {showMap && (
          <div className={styles.mapSection}>
            <AnalysisMap
              onShapeCreated={handleShapeCreated}
              onShapeEdited={handleShapeEdited}
              onMarkerAdded={handleMarkerAdded}
              onMarkerRemoved={handleMarkerRemoved}
              boundary={boundary}
              bufferZone={bufferZone}
              markers={markers}
              center={mapCenter}
              zoom={mapZoom}
              omgevingsData={omgevingsData}
              activeFilters={activeFilters}
              voorzieningen={voorzieningen?.items || null}
              bedrijvenOverpass={bedrijvenOverpass?.items || null}
              organisatieItems={organisaties?.items || null}
            />
            {markers.length > 0 && (
              <div className={styles.markerCount}>
                <svg width="14" height="14" viewBox="0 0 24 32" fill="none">
                  <path d="M3 2V30" stroke="#1E3A2F" strokeWidth="2" strokeLinecap="round"/>
                  <path d="M3 2H20L16 9L20 16H3V2Z" fill="#1E3A2F" fillOpacity="0.9"/>
                </svg>
                {markers.length} markering{markers.length !== 1 ? 'en' : ''}
              </div>
            )}
          </div>
        )}

        {/* Analysis Sidebar */}
        <AnalysisSidebar
          loading={loading}
          buurtNaam={buurtNaam}
          demographics={demographics}
          adviezen={adviezen}
          influenceRadius={influenceRadius}
          areaSqm={areaSqm}
          onRadiusChange={handleRadiusChange}
          onRefresh={handleRefresh}
          onDelete={handleDeleteArea}
          hasArea={!!boundary}
          markerCount={markers.length}
          omgevingsData={omgevingsData}
          onMapCenter={(lat, lng) => { setShowMap(true); setMapCenter([lat, lng]); setMapZoom(16); }}
          bedrijven={bedrijven}
          bedrijvenAdviezen={bedrijvenAdviezen}
          personaHints={personaHints}
          additionalDemographics={additionalDemographics}
          voorzieningen={voorzieningen}
          bedrijvenOverpass={bedrijvenOverpass}
          organisaties={organisaties}
          gebiedsprofiel={gebiedsprofiel}
          onRegenerateProfiel={handleRegenerateProfiel}
          profielLoading={profielLoading}
          fullWidth={!showMap}
          invloedsgebiedAnalyse={invloedsgebiedAnalyse}
          invloedsgebiedLoading={invloedsgebiedLoading}
          woningFilters={woningFilters}
          onWoningFilterChange={handleWoningFilterChange}
          raakvlakAnalyse={raakvlakAnalyse}
          raakvlakLoading={raakvlakLoading}
          onImportToRelatiebeheer={() => setShowImportDialog(true)}
        />
      </div>

      {/* Import naar Relatiebeheer dialog */}
      <ImportToRelatiebeheer
        isOpen={showImportDialog}
        onClose={() => setShowImportDialog(false)}
        projectId={project.id}
        organisaties={organisaties?.items || []}
        bedrijven={bedrijvenOverpass?.items || []}
      />
    </div>
  );
};
